import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { sngpcService } from "services/sngpcService";
import { AtualizarLoteDto, ExcluirLoteDto, CriarInventarioDto, AlterarDataInventarioDto, PesquisarProdutosDto, CriarTransmissaoDto, CriarMovimentoDto, IncluirProdutoInventarioType, EstoqueInventarioType } from "data/sngpc";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useToast } from "providers/ToastProvider";
import { useEmpresas } from "providers/EmpresaProvider";

// Chaves de query para cache
export const sngpcKeys = {
  all: ['sngpc'] as const,
  inventario: () => [...sngpcKeys.all, 'inventario'] as const,
  inventarioAtivo: () => [...sngpcKeys.inventario(), 'ativo'] as const,

  produtos: () => [...sngpcKeys.all, 'produtos'] as const,
  produtosPesquisa: (termo?: string, pageIndex?: number, pageSize?: number) => [...sngpcKeys.produtos(), 'pesquisa', { termo, pageIndex, pageSize }] as const,
  produtosInfinito: (termo?: string) => [...sngpcKeys.produtos(), 'infinito', { termo }] as const,

  transmissoes: () => [...sngpcKeys.all, 'transmissoes'] as const,
  transmissoesPorInventario: (inventarioId: number) => [...sngpcKeys.transmissoes(), inventarioId] as const,
  proximaDataTransmissao: (inventarioId: number) => [...sngpcKeys.transmissoes(), 'proxima-data', inventarioId] as const,
  movimentos: () => [...sngpcKeys.all, 'movimentos'] as const,
  movimento: (id: number) => [...sngpcKeys.movimentos(), id] as const,
};

// Hook para obter inventário ativo
export const useInventarioAtivo = () => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0]; // Usa a primeira empresa selecionada

  return useQuery({
    queryKey: sngpcKeys.inventarioAtivo(),
    queryFn: () => sngpcService.obterInventarioAtivo(empresaId),
    enabled: !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: sngpcKeys.inventarioAtivo(),
      defaultMessage: "Você não possui permissão para consultar inventários."
    }),
  });
};

// Hook para importar estoque controlado
export const useImportarEstoqueControlado = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: () => sngpcService.importarEstoqueControlado(empresaId),
    onSuccess: (data) => {
      if (data.isSuccess) {
        showToast(data.message || "Estoque controlado importado com sucesso!", "success");
        queryClient.invalidateQueries({ queryKey: sngpcKeys.inventario() });
      } else {
        showToast(data.message || "Erro ao importar estoque controlado.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para importar estoque controlado.")
  });
};

// Hook para criar inventário
export const useCriarInventario = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: CriarInventarioDto) => sngpcService.criarInventario(dto, empresaId),
    onSuccess: (data: any) => {
      console.log('Hook onSuccess recebeu:', data);
      if (data.isSuccess) {
        showToast(data.message || "Inventário criado com sucesso!", "success");
        queryClient.invalidateQueries({ queryKey: sngpcKeys.inventario() });
      } else {
        console.log('Erro no backend, mostrando toast:', data.message);
        showToast(data.message || "Erro ao criar inventário.", "error");
      }
    },
    onError: (error: any) => {
      console.log('Hook onError recebeu:', error);
      getMutationErrorHandler("Você não possui permissão para criar inventários.")(error);
    }
  });
};

// Hook para alterar data do inventário
export const useAlterarDataInventario = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: AlterarDataInventarioDto) => sngpcService.alterarDataInventario(dto, empresaId),
    onSuccess: (data) => {
      if (data.isSuccess) {
        showToast(data.message || "Data do inventário alterada com sucesso!", "success");
        queryClient.invalidateQueries({ queryKey: sngpcKeys.inventario() });
      } else {
        showToast(data.message || "Erro ao alterar data do inventário.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para alterar a data do inventário.")
  });
};

// Hook para pesquisar produtos para inventário
export const usePesquisarProdutosParaInventario = (dto: PesquisarProdutosDto) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: sngpcKeys.produtosPesquisa(dto.termo, dto.pageIndex, dto.pageSize),
    queryFn: () => sngpcService.pesquisarProdutosParaInventario(dto, empresaId),
    enabled: !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: sngpcKeys.produtosPesquisa(dto.termo, dto.pageIndex, dto.pageSize),
      defaultMessage: "Você não possui permissão para pesquisar produtos."
    }),
  });
};

// Hook para pesquisar produtos com rolagem infinita
export const usePesquisarProdutosInfinito = (termo?: string) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useInfiniteQuery({
    queryKey: sngpcKeys.produtosInfinito(termo),
    queryFn: ({ pageParam }) => sngpcService.pesquisarProdutosInfinito(pageParam, termo, empresaId),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.isSuccess) return undefined;
      
      const currentPage = lastPage.pageIndex;
      const totalPages = Math.ceil(lastPage.total / 20); // pageSize = 20
      
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!empresaId && !!termo,
    retry: getQueryRetryHandler({
      queryKey: sngpcKeys.produtosInfinito(termo),
      defaultMessage: "Você não possui permissão para pesquisar produtos."
    }),
  });
};

// Hook para atualizar lote
export const useAtualizarLote = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: AtualizarLoteDto) => sngpcService.atualizarLote(dto, empresaId),
    onSuccess: (data, variables) => {
      if (data.isSuccess) {
        showToast("Lote atualizado com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.inventario() 
        });
      } else {
        showToast(data.message || "Erro ao atualizar lote.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para atualizar lotes.")
  });
};

// Hook para excluir lote
export const useExcluirLote = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: ExcluirLoteDto) => sngpcService.excluirLote(dto, empresaId),
    onSuccess: (data, variables) => {
      if (data.isSuccess) {
        showToast("Lote excluído com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.inventario() 
        });
      } else {
        showToast(data.message || "Erro ao excluir lote.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para excluir lotes.")
  });
};

// Hook para incluir produto no inventário
export const useIncluirProdutoInventario = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dados: IncluirProdutoInventarioType) => {
      // Converter os dados para o formato esperado pelo serviço
      const itens: Partial<EstoqueInventarioType>[] = dados.lotes.map(lote => ({
        produtoId: dados.produtoId,
        numero: lote.lote, // Backend espera 'numero' para o lote
        lote: lote.lote,   // Manter compatibilidade
        quantidade: lote.quantidade,
        rms: lote.rms,
        // Incluir informações cadastrais se fornecidas
        lista: dados.lista,
        classeTerapeutica: dados.classeTerapeutica,
        receituario: dados.receituario,
        unidade: dados.unidade
      }));
      
      console.log('Enviando itens para backend:', itens);
      
      return sngpcService.incluirProdutos(itens, empresaId);
    },
    onSuccess: (data) => {
      if (data.isSuccess) {
        showToast("Produto incluído no inventário com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.inventario() 
        });
      } else {
        showToast(data.message || "Erro ao incluir produto no inventário.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para incluir produtos no inventário.")
  });
};

// Hook para validar inventário
export const useValidarInventario = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: () => sngpcService.validarInventario(empresaId),
    onSuccess: (data) => {
      if (data.isSuccess) {
        showToast("Inventário validado com sucesso!", "success");
        queryClient.invalidateQueries({ queryKey: sngpcKeys.inventario() });
      } else {
        showToast(data.message || "Erro ao validar inventário.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para validar inventários.")
  });
};

// Hook para finalizar inventário
export const useFinalizarInventario = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: () => sngpcService.finalizarInventario(empresaId),
    onSuccess: (data) => {
      if (data.isSuccess) {
        showToast("Inventário finalizado com sucesso!", "success");
        queryClient.invalidateQueries({ queryKey: sngpcKeys.inventario() });
      } else {
        showToast(data.message || "Erro ao finalizar inventário.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para finalizar inventários.")
  });
};

// Hook para exportar XML
export const useExportarXml = () => {
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: () => sngpcService.exportarXmlInventario(empresaId),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventario_sngpc_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast("XML exportado com sucesso!", "success");
    },
    onError: getMutationErrorHandler("Você não possui permissão para exportar XML.")
  });
};

// Hook para transmissões
export const useTransmissoes = (inventarioId?: number) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: sngpcKeys.transmissoesPorInventario(inventarioId!),
    queryFn: () => sngpcService.listarTransmissoes(inventarioId!, empresaId),
    enabled: !!inventarioId && !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: sngpcKeys.transmissoesPorInventario(inventarioId!),
      defaultMessage: "Você não possui permissão para consultar transmissões."
    }),
  });
};

// Hook para próxima data de transmissão
export const useProximaDataTransmissao = (inventarioId?: number) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: sngpcKeys.proximaDataTransmissao(inventarioId!),
    queryFn: () => sngpcService.obterProximaDataTransmissao(inventarioId!, empresaId),
    enabled: !!inventarioId && !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: sngpcKeys.proximaDataTransmissao(inventarioId!),
      defaultMessage: "Você não possui permissão para consultar próxima data de transmissão."
    }),
  });
};

// Hook para criar transmissão
export const useCriarTransmissao = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: CriarTransmissaoDto) => sngpcService.criarTransmissao(dto, empresaId),
    onSuccess: (data, variables) => {
      if (data.isSuccess) {
        showToast("Transmissão criada com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.transmissoesPorInventario(variables.inventarioId) 
        });
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.proximaDataTransmissao(variables.inventarioId) 
        });
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para criar transmissões.")
  });
};

// Hook para remover última transmissão
export const useRemoverUltimaTransmissao = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (inventarioId: number) => sngpcService.removerUltimaTransmissao(inventarioId, empresaId),
    onSuccess: (data, inventarioId) => {
      if (data.isSuccess) {
        showToast("Última transmissão removida com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.transmissoesPorInventario(inventarioId) 
        });
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.proximaDataTransmissao(inventarioId) 
        });
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para remover transmissões.")
  });
};

// === HOOKS DE MOVIMENTOS ===

// Hook para listar movimentos
export const useMovimentos = (inventarioId?: number, filtros?: {
  tipo?: string;
  dataInicio?: string;
  dataFim?: string;
  termo?: string;
}) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: [...sngpcKeys.movimentos(), inventarioId, filtros],
    queryFn: () => sngpcService.listarMovimentos(inventarioId!, empresaId, filtros),
    enabled: !!inventarioId && !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: [...sngpcKeys.movimentos(), inventarioId, filtros],
      defaultMessage: "Você não possui permissão para consultar movimentos."
    }),
  });
};

// Hook para obter movimento específico
export const useMovimento = (id?: number) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: sngpcKeys.movimento(id!),
    queryFn: () => sngpcService.obterMovimento(id!, empresaId),
    enabled: !!id && !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: sngpcKeys.movimento(id!),
      defaultMessage: "Você não possui permissão para consultar movimento."
    }),
  });
};

// Hook para criar movimento
export const useCriarMovimento = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (dto: CriarMovimentoDto) => sngpcService.criarMovimento(dto, empresaId),
    onSuccess: (data, variables) => {
      if (data.isSuccess) {
        showToast("Movimento criado com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: [...sngpcKeys.movimentos(), variables.inventarioId] 
        });
        queryClient.invalidateQueries({ 
          queryKey: [...sngpcKeys.all, 'estatisticas'] 
        });
      } else {
        showToast(data.message || "Erro ao criar movimento.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para criar movimentos.")
  });
};

// Hook para editar movimento
export const useEditarMovimento = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: Partial<CriarMovimentoDto> }) => 
      sngpcService.editarMovimento(id, dto, empresaId),
    onSuccess: (data, variables) => {
      if (data.isSuccess) {
        showToast("Movimento atualizado com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.movimento(variables.id) 
        });
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.movimentos() 
        });
        queryClient.invalidateQueries({ 
          queryKey: [...sngpcKeys.all, 'estatisticas'] 
        });
      } else {
        showToast(data.message || "Erro ao atualizar movimento.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para editar movimentos.")
  });
};

// Hook para excluir movimento
export const useExcluirMovimento = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();
  const { showToast } = useToast();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: (id: number) => sngpcService.excluirMovimento(id, empresaId),
    onSuccess: (data, id) => {
      if (data.isSuccess) {
        showToast("Movimento excluído com sucesso!", "success");
        queryClient.invalidateQueries({ 
          queryKey: sngpcKeys.movimentos() 
        });
        queryClient.invalidateQueries({ 
          queryKey: [...sngpcKeys.all, 'estatisticas'] 
        });
      } else {
        showToast(data.message || "Erro ao excluir movimento.", "error");
      }
    },
    onError: getMutationErrorHandler("Você não possui permissão para excluir movimentos.")
  });
};

// Hook para obter estatísticas de movimentos
export const useEstatisticasMovimentos = (inventarioId?: number) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: [...sngpcKeys.all, 'estatisticas', inventarioId],
    queryFn: () => sngpcService.obterEstatisticasMovimentos(inventarioId!, empresaId),
    enabled: !!inventarioId && !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: [...sngpcKeys.all, 'estatisticas', inventarioId],
      defaultMessage: "Você não possui permissão para consultar estatísticas."
    }),
  });
};

// Hook para obter janela temporal ativa
export const useJanelaTemporalAtiva = (inventarioId?: number) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useQuery({
    queryKey: [...sngpcKeys.all, 'janela-temporal', inventarioId],
    queryFn: () => sngpcService.obterJanelaTemporalAtiva(inventarioId!, empresaId),
    enabled: !!inventarioId && !!empresaId,
    retry: getQueryRetryHandler({
      queryKey: [...sngpcKeys.all, 'janela-temporal', inventarioId],
      defaultMessage: "Você não possui permissão para consultar janela temporal."
    }),
  });
};

// Hook para importar lotes vendidos
export const useImportarLotesVendidos = () => {
  const { getMutationErrorHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: ({ inventarioId, dataInicio, dataFim }: { 
      inventarioId: number; 
      dataInicio: string; 
      dataFim: string; 
    }) => sngpcService.importarLotesVendidos(inventarioId, dataInicio, dataFim, empresaId),
    onError: getMutationErrorHandler("Você não possui permissão para importar lotes vendidos."),
  });
};

// Hook para importar lotes comprados
export const useImportarLotesComprados = () => {
  const { getMutationErrorHandler } = usePermissionModal();
  const { selectedIds } = useEmpresas();
  const empresaId = selectedIds[0];

  return useMutation({
    mutationFn: ({ inventarioId, dataInicio, dataFim }: { 
      inventarioId: number; 
      dataInicio: string; 
      dataFim: string; 
    }) => sngpcService.importarLotesComprados(inventarioId, dataInicio, dataFim, empresaId),
    onError: getMutationErrorHandler("Você não possui permissão para importar lotes comprados."),
  });
}; 